import { create } from 'zustand';
import {
  customerService,
  type Customer,
  type CustomerFilter,
  type CreateCustomerDto,
  type UpdateCustomerDto,
  type CustomerSegment,
} from '@services/index';

interface CustomersState {
  // Data
  customers: Customer[];
  customer: Customer | null;
  segments: CustomerSegment[];
  statusCounts: Record<string, number>;

  // Pagination
  total: number;
  page: number;
  limit: number;
  totalPages: number;

  // Filters
  filters: CustomerFilter;

  // Loading states
  isLoading: boolean;
  isLoadingCustomer: boolean;
  isUpdating: boolean;
  error: string | null;

  // Actions
  fetchCustomers: (filter?: CustomerFilter, opts?: { silent?: boolean }) => Promise<void>;
  fetchCustomer: (id: string) => Promise<void>;
  createCustomer: (dto: CreateCustomerDto) => Promise<Customer>;
  updateCustomer: (id: string, dto: UpdateCustomerDto) => Promise<Customer>;
  deleteCustomer: (id: string) => Promise<void>;
  fetchStatusCounts: () => Promise<void>;
  fetchSegments: () => Promise<void>;
  setFilters: (filters: CustomerFilter) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  reset: () => void;
  resetCustomer: () => void;
}

export const useCustomersStore = create<CustomersState>()((set, get) => ({
  // Initial state
  customers: [],
  customer: null,
  segments: [],
  statusCounts: {},
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  filters: {},
  isLoading: false,
  isLoadingCustomer: false,
  isUpdating: false,
  error: null,

  // Fetch customers list.
  // `opts.silent` is used by background polling (see CustomersPage): it refreshes
  // the data in place without toggling the full-page loading spinner or clearing
  // the current rows, and a transient failure is swallowed (no error banner) so
  // an open, working list is never disrupted by a background refresh.
  fetchCustomers: async (filter, opts) => {
    const currentFilters = filter || get().filters;
    const silent = opts?.silent ?? false;
    set(
      silent
        ? { filters: currentFilters }
        : { isLoading: true, error: null, filters: currentFilters },
    );

    try {
      const response = await customerService.getCustomers({
        ...currentFilters,
        page: currentFilters.page || get().page,
        limit: currentFilters.limit || get().limit,
        // The register shows each row's active ticket count, which costs the
        // BFF an extra ticket-service call — ask for it only here, not from
        // every other caller of getCustomers (pickers, merge candidates, etc).
        withEngagement: true,
      });

      set({
        customers: response.data,
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages,
        isLoading: false,
      });
    } catch (error) {
      if (silent) {
        // Keep the existing rows/state on a failed background poll.
        console.error('Background customer refresh failed:', error);
        return;
      }
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch customers',
        isLoading: false,
      });
    }
  },

  // Fetch single customer
  fetchCustomer: async (id) => {
    set({ isLoadingCustomer: true, error: null });

    try {
      const customer = await customerService.getCustomer(id);
      set({ customer, isLoadingCustomer: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch customer',
        isLoadingCustomer: false,
      });
    }
  },

  // Create customer
  createCustomer: async (dto) => {
    set({ isUpdating: true, error: null });

    try {
      const customer = await customerService.createCustomer(dto);
      set((state) => ({
        customers: [customer, ...state.customers],
        total: state.total + 1,
        isUpdating: false,
      }));
      return customer;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create customer',
        isUpdating: false,
      });
      throw error;
    }
  },

  // Update customer
  updateCustomer: async (id, dto) => {
    set({ isUpdating: true, error: null });

    try {
      const updated = await customerService.updateCustomer(id, dto);
      set((state) => ({
        customers: state.customers.map((c) => (c.id === id ? updated : c)),
        customer: state.customer?.id === id ? updated : state.customer,
        isUpdating: false,
      }));
      return updated;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update customer',
        isUpdating: false,
      });
      throw error;
    }
  },

  // Delete customer
  deleteCustomer: async (id) => {
    set({ isUpdating: true, error: null });

    try {
      await customerService.deleteCustomer(id);
      set((state) => ({
        customers: state.customers.filter((c) => c.id !== id),
        total: state.total - 1,
        customer: state.customer?.id === id ? null : state.customer,
        isUpdating: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete customer',
        isUpdating: false,
      });
      throw error;
    }
  },

  // Fetch status counts
  fetchStatusCounts: async () => {
    try {
      const counts = await customerService.getStatusCounts();
      set({ statusCounts: counts });
    } catch (error) {
      console.error('Failed to fetch status counts:', error);
    }
  },

  // Fetch segments
  fetchSegments: async () => {
    try {
      const segments = await customerService.getSegments({ isActive: true });
      set({ segments });
    } catch (error) {
      console.error('Failed to fetch segments:', error);
    }
  },

  // Set filters and refetch
  setFilters: (filters) => {
    set({ filters, page: 1 });
    get().fetchCustomers({ ...filters, page: 1 });
  },

  // Set page and refetch
  setPage: (page) => {
    set({ page });
    get().fetchCustomers({ ...get().filters, page });
  },

  // Change page size — reset to page 1 so the user doesn't end up past the
  // last page (e.g. they were on page 6 of 10 with limit 20, then bumped
  // to limit 100 which only has 2 pages).
  setLimit: (limit) => {
    set({ limit, page: 1 });
    get().fetchCustomers({ ...get().filters, page: 1, limit });
  },

  // Reset all state
  reset: () => {
    set({
      customers: [],
      customer: null,
      segments: [],
      statusCounts: {},
      total: 0,
      page: 1,
      totalPages: 0,
      filters: {},
      isLoading: false,
      isLoadingCustomer: false,
      isUpdating: false,
      error: null,
    });
  },

  // Reset just the current customer
  resetCustomer: () => {
    set({ customer: null, isLoadingCustomer: false });
  },
}));
